export type ChangeBounds = { x: number; y: number; w: number; h: number };
export type ChangeFact = {
  id: string;
  type: string;
  label: string;
  pageId: string;
  scope: string;
  scopeName: string;
  bounds: ChangeBounds;
  signature: string;
  text?: string;
  threadId?: string;
};
export type ChangeTarget = {
  before: ChangeFact | null;
  after: ChangeFact | null;
};
export type ChangeItem = {
  seq: number;
  actor: string;
  name: string;
  started: number;
  ended: number;
  scope: string;
  scopeName: string;
  targets: ChangeTarget[];
  seen: number[];
};
export type ChangePage = {
  items: ChangeItem[];
  epoch: string;
  head: number;
  cursor: number | null;
  unread: number;
  oldest: number;
  started: number;
  truncated: boolean;
};
export type ChangeReceipt = { seq: number; targets: number[] };
export function targetFact(target: ChangeTarget) {
  return target.after ?? target.before!;
}
export function changeSummary(item: Pick<ChangeItem, "targets">) {
  const counts = new Map<string, number>();
  for (const t of item.targets) {
    const kind = !t.before ? "Added" : !t.after ? "Removed" : "Updated";
    const type = targetFact(t).type;
    const key = `${kind} ${type}`;
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  return [...counts]
    .map(([key, n]) => {
      const space = key.indexOf(" "),
        verb = key.slice(0, space),
        type = key.slice(space + 1);
      if (type === "sketch")
        return `${verb} a sketch${n > 1 ? ` (${n} strokes)` : ""}`;
      return `${verb} ${n === 1 ? (/^[aeiou]/.test(type) ? "an" : "a") : n} ${type}${n === 1 ? "" : "s"}`;
    })
    .join(" · ");
}
export function readReceipts(value: unknown): ChangeReceipt[] {
  if (!Array.isArray(value) || value.length > 50)
    throw new Error("Invalid seen changes");
  return value.map((entry: unknown) => {
    if (
      !entry ||
      typeof entry !== "object" ||
      !("seq" in entry) ||
      !("targets" in entry) ||
      !Number.isSafeInteger(entry.seq) ||
      Number(entry.seq) < 1 ||
      !Array.isArray(entry.targets) ||
      entry.targets.length > 50 ||
      entry.targets.some(
        (n: unknown) =>
          !Number.isInteger(n) || Number(n) < 0 || Number(n) >= 50,
      )
    )
      throw new Error("Invalid seen changes");
    return {
      seq: Number(entry.seq),
      targets: [...new Set(entry.targets as number[])],
    };
  });
}

export function readInteractions(
  value: unknown,
): { id: string; signature: string }[] {
  if (!Array.isArray(value) || value.length > 50)
    throw new Error("Invalid interaction");
  return value.map((v: unknown) => {
    if (
      !v ||
      typeof v !== "object" ||
      !("id" in v) ||
      !("signature" in v) ||
      typeof v.id !== "string" ||
      v.id.length > 128 ||
      typeof v.signature !== "string" ||
      v.signature.length > 80
    )
      throw new Error("Invalid interaction");
    return { id: v.id, signature: v.signature };
  });
}
